import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../Store";
import {
  allMessagesStore,
  setAllHistoryMessages,
  setLastNodeResponse,
} from "../../Store/Slices/socket/AllMessagesSlice";
import { botInfoStore } from "../../Store/Slices/socket/BotInfoSlice";
import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { IEvents, OEvents } from "../../Services/IframeHostService/models";

// Merges an incoming relayed message array into the current one instead of
// blindly replacing it. Two independent windows each racing to relay their
// own view of the conversation means either side's relay can legitimately
// arrive "behind" the other's most recent local change (e.g. the PiP's own
// initial-history-sync reply landing after it has already started a fresh
// exchange locally) — a plain replace in that situation clobbers the newer
// state with a stale one. Merging by identity (message_id, falling back to
// value+isUser for older messages that predate that field) instead of
// gating the whole array behind an all-or-nothing guard means every known
// message is kept no matter which side or which order relays arrive in,
// and a matching message's latest content (e.g. a streaming reply's
// growing text) always wins over an older version of the same message.
function mergeMessages(current: any[], incoming: any[]): any[] {
  // Messages without a message_id (only older ones predating that field —
  // every message sent through AppInputHandler.tsx or streamed through
  // getNewMessagesForSmartAI already carries one) fall back to matching by
  // value+isUser. That alone would collapse two genuinely distinct
  // messages with identical text (e.g. the user sending "yes" twice) into
  // a single occurrence — counting each value+isUser combination's running
  // occurrence index as part of its identity keeps repeats distinct (the
  // 1st "yes" only ever matches another array's 1st "yes"), while a
  // message's own repeated relays (the same occurrence, same position)
  // still resolve to the same identity as before.
  const occurrenceCounts = new Map<string, number>();
  const identityOf = (m: any) => {
    if (m?.message_id != null) return `id:${m.message_id}`;
    const base = `v:${m?.isUser}:${m?.value}`;
    const occurrence = occurrenceCounts.get(base) ?? 0;
    occurrenceCounts.set(base, occurrence + 1);
    return `${base}#${occurrence}`;
  };
  const incomingByIdentity = new Map(incoming.map((m) => [identityOf(m), m]));
  occurrenceCounts.clear();
  const seen = new Set<string>();
  const merged: any[] = [];
  // Walk `current` first so its ordering is preserved, taking the incoming
  // version of any message both sides know about (it may be more complete
  // — e.g. a streamed reply's fuller text).
  for (const existing of current) {
    const key = identityOf(existing);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(incomingByIdentity.get(key) ?? existing);
  }
  // Anything only `incoming` knows about (a genuinely new message) is
  // appended in its own relative order.
  occurrenceCounts.clear();
  for (const candidate of incoming) {
    const key = identityOf(candidate);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(candidate);
  }
  return merged;
}

// The primary widget instance and the PiP copy the loader opens (see
// openPipWindow() in geta-tracking-scripts) run as two entirely separate
// widget iframes, each with its own socket connection and its own
// allMessages store — sending a message in one never appeared in the other
// until that other side re-fetched history. Both sides relay their own
// allMessages to the loader on every change, and merge whatever the other
// side last sent into their own state, keeping them consistent no matter
// which one the user is actually typing into or which order relays arrive.
const useCrossWindowMessageSync = () => {
  const dispatch = useAppDispatch();
  const { allMessages, lastNodeResponse } = useAppSelector(allMessagesStore);
  const { isPipInstance, chatOpened } = useAppSelector(botInfoStore);
  // Distinguishes "this update came from applying a relay from the other
  // window" from "this update is new local activity that should itself be
  // relayed out" — without it, applying an incoming relay would immediately
  // re-send it back to where it came from (an infinite ping-pong).
  const isApplyingRemoteUpdateRef = useRef(false);
  const hasReceivedAnyRelayRef = useRef(false);
  const allMessagesRef = useRef(allMessages);
  allMessagesRef.current = allMessages;
  // Whether a PiP copy currently exists to relay to — only meaningful on
  // the primary instance (the PiP copy always has exactly one relay
  // partner, itself never opens another PiP, so it always relays). Without
  // this, the primary posted its entire message list — including every
  // streamed token during a SmartAI reply — to the loader on every change,
  // for every visitor on every page, even when no PiP window exists to
  // receive it.
  const pipActiveRef = useRef(false);

  useEffect(() => {
    const commService = HostWindowCommService.getInstance();
    commService.registerEventListener(IEvents.CHATBOT_PIP_OPENED, () => {
      pipActiveRef.current = true;
    });
    commService.registerEventListener(IEvents.CHATBOT_PIP_CLOSED, () => {
      pipActiveRef.current = false;
    });
  }, []);

  useEffect(() => {
    if (isApplyingRemoteUpdateRef.current) {
      isApplyingRemoteUpdateRef.current = false;
      return;
    }
    if (!isPipInstance && !pipActiveRef.current) return;
    const commService = HostWindowCommService.getInstance();
    commService.postMessage({
      event_type: OEvents.CHATBOT_MESSAGES_SYNC,
      data: { messages: allMessages, lastNodeResponse },
    });
  }, [allMessages, lastNodeResponse, isPipInstance]);

  useEffect(() => {
    const commService = HostWindowCommService.getInstance();
    commService.registerEventListener(
      IEvents.CHATBOT_MESSAGES_SYNC,
      (event: any) => {
        const payload = event?.data?.data;
        const data = payload?.messages;
        const incomingLastNodeResponse = payload?.lastNodeResponse;
        if (!Array.isArray(data)) return;
        hasReceivedAnyRelayRef.current = true;
        // This listener registers on BOTH the primary instance and the PiP
        // copy (each relays to the other). Merge rather than replace, so
        // whichever side's relay happens to arrive doesn't clobber newer
        // local state the other side doesn't know about yet.
        const current = allMessagesRef.current ?? [];
        const merged = mergeMessages(current, data);
        const changed =
          merged.length !== current.length ||
          merged.some((m: any, i: number) => m !== current[i]);
        if (!changed) return;
        isApplyingRemoteUpdateRef.current = true;
        dispatch(setAllHistoryMessages(merged));
        // The composer's own visibility (AppInputHandler.tsx) is gated on
        // lastInputBoxType, which is derived only from lastNodeResponse —
        // set solely by whichever instance actually received a fresh
        // backend response. Without relaying this too, the PiP copy (which
        // no longer runs its own REST fetch — see MainUI.tsx) never has a
        // lastNodeResponse of its own, so lastInputBoxType stays at its
        // null initial value and the composer never renders there.
        if (incomingLastNodeResponse !== undefined) {
          dispatch(setLastNodeResponse(incomingLastNodeResponse));
        }
      }
    );
  }, [dispatch]);

  useEffect(() => {
    // The PiP copy never does its own REST history fetch (see MainUI.tsx) —
    // it relies entirely on this relay for its conversation state. As soon
    // as it opens the chat panel, ask the primary instance for its current
    // allMessages directly, instead of waiting for the primary's NEXT
    // change to arrive — otherwise the panel would sit empty until the
    // other tab happens to send or receive something new.
    if (!isPipInstance || !chatOpened) return;
    const commService = HostWindowCommService.getInstance();
    const sendRequest = () => {
      commService.postMessage({
        event_type: OEvents.CHATBOT_REQUEST_MESSAGES_SYNC,
        data: {},
      });
    };
    // A single one-shot request can be lost if it's sent before the
    // primary instance's own reply-listener has finished mounting (e.g.
    // right as "Keep chatting here" spawns the PiP window) — there's no
    // ack, so a dropped request previously left the PiP waiting forever
    // with an empty panel and no composer. Retry on a short interval
    // until the first relay of any kind is received, then stop.
    sendRequest();
    const retryInterval = setInterval(() => {
      if (hasReceivedAnyRelayRef.current) {
        clearInterval(retryInterval);
        return;
      }
      sendRequest();
    }, 1000);
    return () => clearInterval(retryInterval);
  }, [isPipInstance, chatOpened]);

  useEffect(() => {
    // The primary instance's side of the above — reply with its current
    // allMessages whenever asked, e.g. right after a PiP copy boots.
    if (isPipInstance) return;
    const commService = HostWindowCommService.getInstance();
    commService.registerEventListener(
      IEvents.CHATBOT_REQUEST_MESSAGES_SYNC,
      () => {
        commService.postMessage({
          event_type: OEvents.CHATBOT_MESSAGES_SYNC,
          data: { messages: allMessages, lastNodeResponse },
        });
      }
    );
  }, [isPipInstance, allMessages, lastNodeResponse]);
};

export default useCrossWindowMessageSync;
